import { useLicensingServiceV1 } from "@rw1/api-services";
import { Licensing_V1 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { useAllUserGroupsForACompany } from "../../../hooks/useAllUserGroupsForACompany";
import { LICENSE_GROUP_ALLOCATIONS_QUERY_KEY } from "../../../hooks/useExternalExperiences";
import { useFetchAll } from "../../../hooks/useFetchAll"; // Import generic hook
import { TExperienceGroupRow } from "./useExperienceGroups";
import { useIncomingLicenses } from "./useIncomingLicenses";

interface UseExternalExperienceGroupsParams {
  releaseChannelId?: string;
  companyId?: string;
  enabled?: boolean;
}

export const useExternalExperienceGroups = ({
  releaseChannelId,
  companyId,
  enabled = true,
}: UseExternalExperienceGroupsParams): {
  loading: boolean;
  rows: TExperienceGroupRow[];
} => {
  const licensingService = useLicensingServiceV1();
  const { licensesByReleaseChannel, isLoading: licenseIsLoading } =
    useIncomingLicenses();

  const license = useMemo(() => {
    if (!releaseChannelId) return null;

    return licensesByReleaseChannel.get(releaseChannelId) ?? null;
  }, [releaseChannelId, licensesByReleaseChannel]);

  const { data: licenseGroupAllocations, isLoading: loadingAllocations } =
    useFetchAll<Licensing_V1.LicenseGroupAllocation>(
      [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, license?.licenseId],
      (page, size) =>
        licensingService.getLicenseGroupAllocations(
          license!.licenseId,
          page,
          size,
        ),
      {
        enabled: !!license?.licenseId && enabled,
        staleTime: 1000 * 60 * 5,
      },
    );

  const { groups: allCompanyGroups, isLoading: loadingGroups } =
    useAllUserGroupsForACompany(companyId, true);

  const uniqueGroupIds = useMemo(() => {
    const gIds = new Set<string>();
    licenseGroupAllocations?.forEach((allocation) => {
      if (allocation.userGroupId && !allocation.isRevoked) {
        gIds.add(allocation.userGroupId);
      }
    });

    return Array.from(gIds);
  }, [licenseGroupAllocations]);

  const validGroups = useMemo(() => {
    if (!allCompanyGroups.length) return [];
    const groupIdSet = new Set(uniqueGroupIds);

    return allCompanyGroups.filter((g) => groupIdSet.has(g.userGroupId));
  }, [allCompanyGroups, uniqueGroupIds]);

  const rows = useMemo(() => {
    const accessMap = new Map<string, Date | null>();
    licenseGroupAllocations?.forEach((allocation) => {
      const groupId = allocation.userGroupId;
      if (groupId && !allocation.isRevoked) {
        accessMap.set(groupId, allocation.expiresAt ?? null);
      }
    });

    return validGroups.map((group) => ({
      userGroupId: group.userGroupId,
      userGroupName: group.userGroupName,
      userCount: group.userCount,
      experienceCount: group.experienceCount,
      expiresAt: accessMap.get(group.userGroupId) ?? null,
    }));
  }, [licenseGroupAllocations, validGroups]);

  return {
    loading: loadingAllocations || loadingGroups || licenseIsLoading,
    rows,
  };
};
